import React from "react";
import { Link } from "react-router-dom";
import { API } from "../lib/api";
import { Button } from "../components/ui/button";
import { ShieldCheck, FileText, Download, ScrollText, Lock, Award, ArrowRight, ExternalLink } from "lucide-react";

const downloadAuth = async (url, filename) => {
  const token = localStorage.getItem("f360_token");
  const r = await fetch(url, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
  if (!r.ok) return;
  const blob = await r.blob();
  const u = URL.createObjectURL(blob);
  const a = document.createElement("a"); a.href = u; a.download = filename; a.click();
  URL.revokeObjectURL(u);
};

const SubModuleCard = ({ to, icon: Icon, title, description, accent = "orange", testid }) => {
  const grad = {
    orange: "from-orange-500/20 to-pink-500/20 border-orange-500/30 hover:border-orange-500/60",
    blue:   "from-sky-500/20 to-indigo-500/20 border-sky-500/30 hover:border-sky-500/60",
    emerald:"from-emerald-500/20 to-teal-500/20 border-emerald-500/30 hover:border-emerald-500/60",
  }[accent];
  return (
    <Link to={to} className={`glass rounded-lg p-5 border bg-gradient-to-br ${grad} transition-all block group`} data-testid={testid}>
      <div className="flex items-start justify-between mb-2">
        <Icon className="w-7 h-7 text-orange-400" />
        <ArrowRight className="w-4 h-4 text-gray-500 group-hover:text-gray-900 group-hover:translate-x-1 transition-all" strokeWidth={2.5} />
      </div>
      <h3 className="text-base font-bold mt-2">{title}</h3>
      <p className="text-xs text-gray-500 mt-1">{description}</p>
    </Link>
  );
};

export default function Compliance() {
  return (
    <div className="space-y-6" data-testid="compliance-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="text-xs text-gray-500">Operations → Compliance</div>
          <h1 className="text-3xl md:text-4xl font-extrabold flex items-center gap-2">
            <Award className="w-8 h-8 text-orange-400" strokeWidth={2.5} /> Compliance
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            GR-aligned ACMS platform · Statutory monitoring, logging, reporting & audit support · Maharashtra Fire Rules 2009 (Amd. 2025)
          </p>
        </div>
        <Button onClick={() => downloadAuth(`${API}/compliance/technical-note.pdf`, "Fire360Pro-ACMS-Technical-Note.pdf")}
          className="f360-gradient border-0" data-testid="download-tech-note-btn">
          <Download className="w-4 h-4 mr-1" strokeWidth={2.5} /> Download Technical Note (PDF)
        </Button>
      </div>

      {/* Sub-module quick-links */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="compliance-submodules">
        <SubModuleCard to="/reports" icon={FileText} title="Reports & Logs" description="System-level reports · PDF & Excel exports · audit-grade evidence per site" accent="blue" testid="link-reports" />
        <SubModuleCard to="/gr-compliance" icon={ShieldCheck} title="GR Compliance Logs" description="Diesel weekly test · Manual override audit · Monthly GR test · ACMS certificate status" accent="orange" testid="link-gr-compliance" />
        <SubModuleCard to="/certificate" icon={Award} title="ACMS Certificate Management" description="Issue, track and verify ACMS compliance certificates with QR-code public verification" accent="emerald" testid="link-certificate" />
      </div>

      {/* Technical Note (inline view) */}
      <section className="glass rounded-lg p-6 space-y-5" data-testid="technical-note-inline">
        <header className="border-b border-gray-200 pb-4">
          <div className="text-[10px] uppercase tracking-[0.2em] text-orange-700 font-bold mb-1">Technical Note</div>
          <h2 className="text-2xl font-extrabold">Fire360Pro GR-Aligned ACMS Platform</h2>
          <p className="text-xs text-gray-500 mt-1">For attachment to Form A-1, Form B-1, technical submittals and client proposals</p>
        </header>

        <Block title="Purpose">
          <p>This technical note is intended for attachment to Form A-1, Form B-1, technical submittals, and client proposals for projects using Fire360Pro as the Automated Continuous Monitoring System (ACMS) platform. The <b>Maharashtra Fire Prevention and Life Safety Measures (Amendment) Rules, 2025</b> introduce ACMS into the regulatory framework and define Form A-1 and Form B-1 as the statutory certificate formats to be issued by Licensed Agencies for ACMS execution and compliance.</p>
          <p>Fire360Pro is therefore positioned as a <b>GR-aligned ACMS platform</b> that enables statutory monitoring, logging, alerting, reporting, and audit support for fire-fighting systems in buildings, while the actual statutory certification remains project-specific and is issued by the Licensed Agency for the concerned building under the prescribed forms.</p>
        </Block>

        <Block title="Regulatory Position">
          <p>The amended Maharashtra Fire Rules require implementation of an automated continuous monitoring system for fire-fighting systems in applicable buildings and establish Form A-1 and Form B-1 for certification of ACMS provision and compliance.</p>
          <p>Accordingly, Fire360Pro is <b>not represented as a government-approved standalone software product certificate.</b> Instead, it is the ACMS platform deployed for a specific site, through which the Licensed Agency, building owner, and fire authority can monitor and document the operational status of the required fire-fighting systems.</p>
        </Block>

        <Block title="Fire360Pro Platform Scope">
          <p>Based on the current deployed platform structure, Fire360Pro includes modules and interfaces relevant to ACMS implementation:</p>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mt-2 text-xs">
            {[
              "Dashboard", "ACMS Live Monitor", "Hydrant System", "Sprinkler System", "Fire Water Tank",
              "Fire Detection", "Fire Alarm", "Ventilation", "Lifts Module", "Fire Command Center",
              "Reports & Logs", "GR Compliance Logs", "ACMS Certificate Records", "Audit Log",
              "Multi-site Administrative Controls",
            ].map((m) => (
              <div key={m} className="bg-gray-50 rounded px-2 py-1.5 text-gray-700">{m}</div>
            ))}
          </div>
          <p className="mt-3">The platform also includes site health views, organisation-level monitoring, alert handling, certificate status tracking, and GR compliance indicators intended to support central monitoring and evidence generation for statutory and operational review.</p>
        </Block>

        <Block title="Functional Alignment With GR Intent">
          <p>Fire360Pro is designed to support continuous monitoring of critical fire-fighting and life-safety infrastructure such as hydrant systems, sprinkler systems, water storage, alarm and detection status, ventilation and lift-related fire interfaces, and other connected field inputs as configured for the project.</p>
          <p>For each implementation, the final monitored point list, alarm matrix, logic, communication architecture, and reporting scope are project-mapped against the fire protection systems installed at site, the approved drawings, and the requirements of the applicable Maharashtra Fire Rules, NBC provisions, and relevant BIS standards referenced by the project team and Licensed Agency.</p>
        </Block>

        <Block title="Role in Form A-1 and Form B-1 Submittals">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-2">
            <div className="bg-orange-500/5 border border-orange-500/20 rounded-lg p-3">
              <div className="text-[10px] uppercase tracking-widest text-orange-700 font-bold mb-1">Form A-1 (Provision)</div>
              <p className="text-xs text-gray-600">Fire360Pro may be described as the digital ACMS layer used for data acquisition, status visualisation, event logging, alarm generation, report generation, and compliance record support for the building's fire-fighting systems.</p>
            </div>
            <div className="bg-blue-500/5 border border-blue-500/20 rounded-lg p-3">
              <div className="text-[10px] uppercase tracking-widest text-blue-700 font-bold mb-1">Form B-1 (Continued Compliance)</div>
              <p className="text-xs text-gray-600">Fire360Pro may be referenced as the operating platform through which the ongoing health, event history, supervisory alerts, and compliance evidence of the installed ACMS are reviewed by the Licensed Agency during inspection and certification of continued compliance.</p>
            </div>
          </div>
        </Block>

        <Block title="Recommended Description for Proposals and Annexures">
          <blockquote className="bg-orange-500/5 border-l-4 border-orange-400 rounded p-4 italic text-gray-700" data-testid="proposal-description">
            Fire360Pro is a <b>GR-aligned Automated Continuous Monitoring System (ACMS) platform</b> designed for continuous monitoring, alerting, logging, reporting, and audit support of building fire-fighting and life-safety systems in accordance with the functional intent of <b>GR No. MFS-2023/C.R.370/UD-14</b> and the Maharashtra Fire Prevention and Life Safety Measures Rules, 2009 as amended. Statutory certification under <b>Form A-1</b> and <b>Form B-1</b> remains site-specific and is issued by the Licensed Agency for the respective building installation.
          </blockquote>
        </Block>

        <Block title="Compliance Boundaries" icon={Lock}>
          <p>This note must be used with the clear understanding that statutory acceptance depends on the complete site implementation, including <b>approved design, installed field hardware, communication reliability, monitored parameters, testing, commissioning, and certification</b> by the Licensed Agency under the prescribed statutory forms.</p>
          <p>Accordingly, Fire360Pro is described as <b>GR-aligned and deployment-ready</b> for statutory ACMS projects, rather than as independently government-certified under the GR as a standalone software product.</p>
        </Block>

        <footer className="border-t border-gray-200 pt-3 text-[10px] text-gray-500 flex items-center justify-between flex-wrap gap-2">
          <span>Reference: <b>GR No. MFS-2023/C.R.370/UD-14</b> · Maharashtra Fire Prevention and Life Safety Measures (Amendment) Rules, 2025 · Form A-1 &amp; Form B-1</span>
          <a href="https://mahafireservice.gov.in/" target="_blank" rel="noreferrer" className="text-orange-700 inline-flex items-center gap-1 hover:text-gray-900">Maharashtra DoFES <ExternalLink className="w-3 h-3" strokeWidth={2.5} /></a>
        </footer>
      </section>
    </div>
  );
}

function Block({ title, icon: Icon = ScrollText, children }) {
  return (
    <div className="space-y-2">
      <h3 className="text-sm uppercase tracking-[0.2em] text-orange-700 font-bold flex items-center gap-2">
        <Icon className="w-4 h-4" /> {title}
      </h3>
      <div className="text-sm text-gray-600 space-y-2 leading-relaxed">{children}</div>
    </div>
  );
}
